import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { CrmDetailConfig, CrmDetailProvider } from '@touch4it/ng-swp/detail';
import {
  CrmCompositeSignalConfigParams,
  CrmCompositeSignalParams,
  CrmCompositeSignalSource,
} from '@touch4it/ng-swp/composite-signal';
import { CrmDictionary } from '@touch4it/ng-swp/core/types';
import { map, switchMap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { crmGetUserName } from '@touch4it/ng-swp/user';
import { HttpErrorResponse } from '@angular/common/http';
import { CrmMessageService } from '@touch4it/ng-swp/message';

import { UserModel } from '~/api/users/user.model';
import { UsersApiService } from '~/api/users/users-api.service';
import { CardExtraComponent } from '~/shared/components/card-extra/card-extra.component';
import { CardDetailComponent } from '~/shared/components/card-detail/card-detail.component';
import { catchErrorEmpty } from '~/shared/utils/rxjs/catch-error-empty';

import { UserDetailFormProvider } from '../detail/user-detail-form.provider';
import { UserDetailHandler } from '../detail/user-detail.handler';

type Data = {
  user: UserModel;
  filter?: CrmDictionary;
};

type Params = CrmCompositeSignalParams<CrmDictionary, CrmDictionary>;

@Injectable()
export class UserProfileProvider extends CrmDetailProvider<Data, Params> {
  private userService = inject(UsersApiService);
  private message = inject(CrmMessageService);
  private router = inject(Router);

  private detailProvider = inject(UserDetailFormProvider);
  private detailHandler!: UserDetailHandler;

  private get user() {
    return computed(() => this.dataSignal().user);
  }

  override init(options: { viewKey: string; route: ActivatedRoute; destroyRef: DestroyRef }) {
    this.detailHandler = new UserDetailHandler(
      this.user,
      (data) => this.updateUser(data),
      () => {},
    );

    return super.init(options);
  }

  protected override getComponentConfigSource(): CrmCompositeSignalSource<
    CrmDetailConfig,
    CrmCompositeSignalConfigParams<Data, Params>
  > {
    return {
      header: {
        showBack: true,
        backTitle: 'users.back',
        backClick: () => this.back(),
        title: computed(() => crmGetUserName(this.user())),
        actionsConfig: {
          actions: [
            // Note: No delete action for profile since users shouldn't delete themselves
          ],
        },
      },
      grid: {
        cards: [
          {
            id: 'detail',
            card: {
              title: 'users.profile.title',
              extraTmpl: {
                content: CardExtraComponent,
                contentContext: { handler: this.detailHandler },
              },
              content: CardDetailComponent,
              contentContext: {
                handler: this.detailHandler,
                provider: this.detailProvider,
              },
            },
          },
        ],
      },
    };
  }

  protected override getComponentDataSource(): CrmCompositeSignalSource<Data, Params> {
    return () =>
      this.userService.user$.pipe(
        switchMap((currentUser) => this.userService.getUser(currentUser._id)),
        map((user) => ({ user })),
        catchErrorEmpty((err: HttpErrorResponse) => {
          if (err.status === 404) {
            this.message.error('users.notFound');
          } else {
            this.message.error('generic.sww');
          }

          this.back();
        }),
      );
  }

  private back() {
    this.router.navigate(['/'], { replaceUrl: true }).then();
  }

  private updateUser(user: UserModel) {
    this.dataSignal.update(() => ({ user }));
  }
}
